import { Button } from "components/shared/panda/Button"
import { RoundedTitle } from "components/shared/panda/RoundedTitle"
import { getHref } from "lib/helpers"
import { urlForImage } from "lib/sanity.image"
import { PricingCardsItem } from "schemas"
import { HStack, Square, styled, VStack } from "styled-system/jsx"

type Props = PricingCardsItem & {
  index?: number
}

function PricingCardsItem({
  index,
  title,
  price,
  priceUnit,
  priceTip,
  features,
  cta,
  image,
}: Props) {
  return (
    <VStack
      alignItems="start"
      gap={0}
      css={{
        position: "relative",
        maxW: "100%",
        width: "584px",
        py: { base: "26px", lg: "48px" },
        px: { base: "16px", lg: "30px" },
        rounded: "20px",
        color: "white",
        background: index % 2 === 0 ? "#6051D6" : "#2817B0",
      }}>
      {image && (
        <Square
          as="img"
          css={{
            position: "absolute",
            width: { base: "100px", lg: "200px" },
            height: { base: "100px", lg: "200px" },
            top: { base: "-42px", lg: "-72px" },
            right: 0,
            alignSelf: "start",
          }}
          src={urlForImage(image).url()}
          alt={image.alt || ""}
        />
      )}

      <styled.div
        maxW={{
          base: "calc(100% + 30px - 85px)",
          lg: "calc(100% + 30px - 185px)",
        }}>
        <RoundedTitle as="h3" color="#C9C2FF" borderColor="#9FAFFF" responsive>
          {title}
        </RoundedTitle>
      </styled.div>

      <styled.p
        css={{
          fontFamily: "heading",
          fontSize: { base: "24px", lg: "48px" },
          fontWeight: "600",
          lineHeight: "126%",
          mb: 4,
        }}>
        {price}
        <styled.span
          css={{
            fontSize: { base: "12px", lg: "24px" },
            ml: 2,
          }}>
          {priceUnit}
        </styled.span>
      </styled.p>

      <styled.p
        css={{
          color: "#EDEAFF",
          fontSize: { base: "12px", lg: "19px" },
          lineHeight: "133%",
          mb: 6,
        }}>
        {priceTip}
      </styled.p>

      {features.length > 0 && (
        <VStack
          alignItems="start"
          gap={{ base: "4px", lg: "8px" }}
          css={{
            fontSize: { base: "12px", lg: "20px" },
            lineHeight: "133%",
            opacity: 0.8,
            mb: 6,
          }}>
          {features.map((feature, i) => (
            <FeatureListItem key={feature + i}>{feature}</FeatureListItem>
          ))}
        </VStack>
      )}

      {cta && (
        <Button
          variant={index % 2 === 0 ? "filled" : "outline"}
          fillColor={index % 2 === 0 ? "tertiary" : undefined}
          outlineColor={index % 2 === 0 ? undefined : "primary"}
          size="medium"
          href={getHref(cta)}
          responsive
          css={{
            width: "100%",
            mt: "auto",
          }}>
          {cta.text}
        </Button>
      )}
    </VStack>
  )
}

const FeatureListItem = ({ children }) => (
  <HStack alignItems="start" gap={1}>
    <styled.svg
      m={1}
      flex="0"
      minW="max-content"
      hideBelow="lg"
      xmlns="http://www.w3.org/2000/svg"
      width="17px"
      height="17px"
      viewBox="0 0 17 17"
      fill="none">
      <rect width="17" height="17" rx="8.5" fill="#1CD6B5" />
      <path
        d="M7.02474 11.8167C6.87159 11.8167 6.73121 11.7657 6.61634 11.6508L4.07659 9.11108C3.84687 8.88135 3.84687 8.524 4.07659 8.29428C4.30632 8.06455 4.66367 8.06455 4.8934 8.29428L7.03751 10.4256L12.1298 5.34612C12.3595 5.1164 12.7169 5.1164 12.9466 5.34612C13.1763 5.57585 13.1763 5.9332 12.9466 6.16293L7.44591 11.6508C7.31828 11.7657 7.1779 11.8167 7.02474 11.8167Z"
        fill="white"
      />
    </styled.svg>
    <styled.svg
      m={1}
      minW="max-content"
      hideFrom="lg"
      xmlns="http://www.w3.org/2000/svg"
      width="10px"
      height="11px"
      viewBox="0 0 10 11"
      fill="none">
      <rect
        x="0.753906"
        y="0.356689"
        width="9.08219"
        height="9.08219"
        rx="4.5411"
        fill="#1CD6B5"
      />
      <path
        d="M4.5167 6.66932C4.43488 6.66932 4.35988 6.64204 4.29851 6.58068L2.94166 5.22382C2.81893 5.10109 2.81893 4.91018 2.94166 4.78745C3.06439 4.66472 3.2553 4.66472 3.37803 4.78745L4.52352 5.92612L7.24404 3.21241C7.36677 3.08968 7.55769 3.08968 7.68042 3.21241C7.80315 3.33514 7.80315 3.52605 7.68042 3.64878L4.7417 6.58068C4.67352 6.64204 4.59852 6.66932 4.5167 6.66932Z"
        fill="white"
      />
    </styled.svg>
    <p>{children}</p>
  </HStack>
)

export default PricingCardsItem
export type { Props as PricingCardsItemProps }
